import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import * as React from 'react';

import { filterListShowsSearch } from '../lib/filter-list-shows-search.js';
import { useFilterListCursor } from '../lib/use-filter-list-cursor.js';
import { cn } from '../lib/utils.js';
import type { DimeFilterListProps } from './DimeFilterListProps.js';
import { DimeFilterListRow } from './DimeFilterListRow.js';
import { DimeFilterListSearch } from './DimeFilterListSearch.js';

const UNLISTED_PICK_LABEL = 'Not in this list';

/** The searchable, ticked list behind every filter (a `DimeFilterCombobox` popover or a filter-panel section); Select All comes in through `searchAction` or sits in the caller's heading (`useFilterSelectAll`). */
export function DimeFilterList(props: DimeFilterListProps) {
  const { label, options, emptyMessage, notice, listClassName, onSearchChange, ref } = props;
  const { searchableListClassName } = props;
  const [search, setSearch] = React.useState('');
  // The pickers' own match, which ignores case and accents, so "cafe" finds "Café".
  const { contains } = ComboboxPrimitive.useFilter();
  const isPicked = (value: string) =>
    props.multiple ? props.value.includes(value) : props.value === value;
  const listedValues = new Set(options.map((option) => option.value));
  const namedPicks = (props.selectedOptions ?? []).filter(
    (picked) => isPicked(picked.value) && !listedValues.has(picked.value),
  );
  const namedValues = new Set(namedPicks.map((picked) => picked.value));
  const pickedValues = props.multiple
    ? props.value
    : props.value === undefined
      ? []
      : [props.value];
  // A pick nobody can name (a stale link, a list cut short) still filters, so it gets a row to untick, numbered when there are several.
  const unnamedValues = pickedValues.filter(
    (value) => !listedValues.has(value) && !namedValues.has(value),
  );
  const unnamedPicks = unnamedValues.map((value, index) => ({
    value,
    label: unnamedValues.length > 1 ? `${UNLISTED_PICK_LABEL} (${index + 1})` : UNLISTED_PICK_LABEL,
  }));
  const unlistedPicks = [...namedPicks, ...unnamedPicks];
  const showSearch = filterListShowsSearch({
    search: props.search,
    serverSearched: onSearchChange != null,
    optionCount: options.length,
  });
  // Text left in a search box that has since gone (the options shrank) no longer filters.
  const term = showSearch ? search.trim() : '';
  const optionMatches =
    onSearchChange != null || term === ''
      ? options
      : options.filter((option) => contains(option.label, term));
  const matches = [...unlistedPicks, ...optionMatches];

  // A list that closes mid-search tells a server-searching caller the search is over.
  const searchRef = React.useRef(search);
  const onSearchChangeRef = React.useRef(onSearchChange);
  React.useLayoutEffect(() => {
    searchRef.current = search;
    onSearchChangeRef.current = onSearchChange;
  });
  React.useEffect(
    () => () => {
      if (searchRef.current !== '') onSearchChangeRef.current?.('');
    },
    [],
  );

  let select: (value: string) => void;
  if (props.multiple) {
    const { value, onValueChange } = props;
    select = (picked) =>
      onValueChange(
        value.includes(picked) ? value.filter((each) => each !== picked) : [...value, picked],
      );
  } else {
    const { value, onValueChange, onPick, clearable = true } = props;
    select = (picked) => {
      // Picking the ticked option again clears it, unless the choice is never empty.
      onValueChange(clearable && value === picked ? undefined : picked);
      onPick?.();
    };
  }

  const baseId = React.useId();
  const listId = `${baseId}-list`;
  const rowId = (index: number) => `${baseId}-row-${index}`;
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const listboxRef = React.useRef<HTMLDivElement>(null);
  // Under a search the keyboard starts on the first match, not on the picks listed above the matches.
  const cursor = useFilterListCursor(matches, term, term === '' ? matches : optionMatches);
  const activeIndex = matches.findIndex((option) => option.value === cursor.activeValue);
  const activeRowId = activeIndex === -1 ? undefined : rowId(activeIndex);

  // The arrows, Home and End move the active row, and Enter, or Space outside the search box, toggles it, as in any listbox.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Keys that compose text through an IME (keyCode 229 in Safari's commit) belong to the search box.
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    const target = event.target as Node;
    const fromSearch = target === searchInputRef.current;
    if (!fromSearch && listboxRef.current?.contains(target) !== true) return;
    if (event.key === 'Enter' || (event.key === ' ' && !fromSearch)) {
      if (cursor.activeValue === undefined) return;
      event.preventDefault();
      select(cursor.activeValue);
      return;
    }
    // In the search box Home and End move the caret.
    if (fromSearch && (event.key === 'Home' || event.key === 'End')) return;
    const next = cursor.stepFor(event.key);
    if (next === undefined) return;
    event.preventDefault();
    cursor.moveTo(next.value);
    const nextIndex = matches.findIndex((option) => option.value === next.value);
    event.currentTarget.ownerDocument
      .getElementById(rowId(nextIndex))
      ?.scrollIntoView?.({ block: 'nearest' });
  };

  return (
    // No padding of its own, so in a panel section the rows sit flush with the section's heading.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- keys bubble here from the search box and the listbox, which own them; the root only routes them.
    <div
      ref={ref}
      tabIndex={-1}
      // The root has no role, so focus given to it (an opening popover) moves on to the listbox, which names the active row.
      onFocus={(event) => {
        if (event.target === event.currentTarget) listboxRef.current?.focus();
      }}
      onKeyDown={handleKeyDown}
      className="group/filter-list flex size-full flex-col overflow-hidden rounded-md bg-transparent p-0 text-popover-foreground outline-none"
    >
      {showSearch && (
        <DimeFilterListSearch
          inputRef={searchInputRef}
          label={label}
          listId={listId}
          activeRowId={activeRowId}
          // Select All would act on rows the typed search hides, so it waits for the box to empty.
          action={search.trim() === '' ? props.searchAction : undefined}
          placeholder={props.searchPlaceholder ?? `Search ${label.toLowerCase()}…`}
          value={search}
          maxLength={props.searchMaxLength}
          onValueChange={(text) => {
            setSearch(text);
            onSearchChange?.(text);
          }}
        />
      )}
      <div
        ref={listboxRef}
        id={listId}
        role="listbox"
        aria-label={label}
        aria-multiselectable={props.multiple ? true : undefined}
        aria-activedescendant={activeRowId}
        // A click on a row leaves focus in the search box, so typing carries on.
        onMouseDown={(event) => {
          if (event.currentTarget.ownerDocument.activeElement === searchInputRef.current) {
            event.preventDefault();
          }
        }}
        // Without a search box a listbox with rows is the Tab stop; with one, the box is, so Shift+Tab leaves the list.
        tabIndex={showSearch || matches.length === 0 ? -1 : 0}
        className={cn(
          'no-scrollbar scroll-py-1 overflow-x-hidden overflow-y-auto rounded-md text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset',
          listClassName,
          showSearch && searchableListClassName,
        )}
      >
        {matches.map((option, index) => (
          <DimeFilterListRow
            key={option.value}
            id={rowId(index)}
            option={option}
            checked={isPicked(option.value)}
            active={option.value === cursor.activeValue}
            onSelect={() => select(option.value)}
            onPointerMove={() => cursor.moveTo(option.value)}
          />
        ))}
      </div>
      {/* Outside the list, which may hold only options, and always mounted so a search that empties the list is announced. */}
      <div role="status">
        {optionMatches.length === 0 && (
          <div className="py-6 text-center text-sm">{emptyMessage}</div>
        )}
        {notice ? <div className="px-2 py-1.5 text-sm text-muted-foreground">{notice}</div> : null}
      </div>
    </div>
  );
}
